import React, { useState, useContext } from "react";
import authFetch from "../../authFetch";
import { userName, locName } from "../../utils/helpers";
import { hapticSuccess, hapticTap, openExternalUrl } from "../../native";
import Modal from "../../shared/Modal";
import TaskExpenseModal from "./TaskExpenseModal";
import TranslateInline from "../../shared/TranslateInline";
import PlatformCtx from "../../state/PlatformCtx";
import { getTaxonomyEntry } from "../../permissions/checks";

function TaskDetailModal({state,dispatch,t,task,onClose,onUpdate,canEdit=true}){
  const {users=[],locations=[],lang,maintenance=[],suppliers=[]}=state;
  const isRTL=lang==="ar";
  const API=typeof window!=="undefined"?window.location.origin:"";
  const platformCtx=useContext(PlatformCtx);
  /* Phase 3.2: resolve display label from taxonomy. Falls back to
     the humanized task_type string so legacy tasks (no taskTypeId)
     still render correctly — see plan §2.4. */
  const _taxEntry=task.taskTypeId?getTaxonomyEntry(state,platformCtx,task.taskTypeId):null;
  const _taxParent=_taxEntry?.parent?getTaxonomyEntry(state,platformCtx,_taxEntry.parent):null;
  const _taxLbl=(e)=>!e?"":(isRTL&&e.labelAr?e.labelAr:e.label);
  const [tab,setTab]=useState("details");
  const [comment,setComment]=useState("");
  const [activityLog,setActivityLog]=useState([]);
  const [logLoaded,setLogLoaded]=useState(false);
  const [showExpense,setShowExpense]=useState(false);
  const [showTaskId,setShowTaskId]=useState(false);
  const [actionLoading,setActionLoading]=useState("");

  const loadLog=async()=>{
    if(logLoaded)return;
    try{
      const r=await authFetch(`${API}/api/tasks?action=activity_log&ws_key=${state.wsKey}&task_id=${task.id}`);
      const j=await r.json();
      setActivityLog(j.logs||[]);setLogLoaded(true);
    }catch{}
  };

  const doTransition=async(newStatus)=>{
    setActionLoading(newStatus);
    try{
      const r=await authFetch(`${API}/api/tasks`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        action:"transition",ws_key:state.wsKey,task_id:task.id,new_status:newStatus,
        actor_id:state.currentUser?.id||"",actor_name:state.currentUser?.name||"",
      })});
      const j=await r.json();
      if(j.error==="expense_required"){setShowExpense(true);setActionLoading("");return;}
      if(j.ok&&j.patch){
        dispatch({type:"UPDATE_TASK",id:task.id,patch:j.patch});
        onUpdate({...task,...j.patch});
        /* Reverse sync: update linked module records */
        const ctx=task.contextPayload||{};
        if(task.sourceModule==="Facility"&&ctx.maintenanceId){
          dispatch({type:"UPDATE_MAINT",id:ctx.maintenanceId,patch:{status:newStatus}});
        }
        if((task.taskType==="grocery_run"||task.sourceModule==="Household")&&ctx.groceryIds){
          (ctx.groceryIds||[]).forEach(gId=>dispatch({type:"UPDATE_GROCERY",id:gId,patch:{status:newStatus}}));
        }
        if(task.sourceModule==="Transport"&&ctx.transportIds){
          (ctx.transportIds||[]).forEach(tId=>dispatch({type:"UPDATE_TRANSPORT",id:tId,patch:{status:newStatus}}));
        }
        (task.linkedEntities||[]).forEach(le=>{
          if(le.type==="grocery")dispatch({type:"UPDATE_GROCERY",id:le.id,patch:{status:newStatus}});
          if(le.type==="maintenance")dispatch({type:"UPDATE_MAINT",id:le.id,patch:{status:newStatus}});
          if(le.type==="transport_request")dispatch({type:"UPDATE_TRANSPORT",id:le.id,patch:{status:newStatus}});
        });
      }
    }catch(e){console.error(e);}
    setActionLoading("");
  };

  const doApproval=async(action)=>{
    setActionLoading(action);
    try{
      const r=await authFetch(`${API}/api/tasks`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        action,ws_key:state.wsKey,task_id:task.id,
        actor_id:state.currentUser?.id||"",actor_name:state.currentUser?.name||"",notes:"",
      })});
      const j=await r.json();
      if(j.ok&&j.patch){dispatch({type:"UPDATE_TASK",id:task.id,patch:j.patch});onUpdate({...task,...j.patch});}
    }catch(e){console.error(e);}
    setActionLoading("");
  };

  const addComment=async()=>{
    if(!comment.trim())return;
    try{
      const r=await authFetch(`${API}/api/tasks`,{method:"POST",headers:{"Content-Type":"application/json"},body:JSON.stringify({
        action:"add_comment",ws_key:state.wsKey,task_id:task.id,text:comment,
        actor_id:state.currentUser?.id||"",actor_name:state.currentUser?.name||"",
      })});
      const j=await r.json();
      if(j.task){dispatch({type:"UPDATE_TASK",id:task.id,patch:j.task});onUpdate(j.task);setComment("");}
    }catch(e){console.error(e);}
  };

  const transitions={pending:["approved","in_progress","cancelled"],approved:["in_progress","cancelled"],in_progress:["completed","cancelled"],completed:["in_progress"],rejected:["pending","cancelled"],cancelled:["pending"]};
  /* Hide the 'approved' transition when this task does not use the
     approval workflow — approval is opt-in per source module, so
     modules like Projects that skip it should not see an Approved
     button mixed in with Start / Cancel. */
  const available=(transitions[task.status]||[]).filter(s=>s!=="approved"||task.requiresApproval);

  const priPill=p=>p==="urgent"||p==="high"?"p-high":p==="medium"?"p-med":"p-low";
  const stPill=s=>s==="completed"?"p-done":s==="in_progress"?"p-wait":s==="rejected"||s==="cancelled"?"p-high":"p-open";
  const fmtStatus=s=>s.replace(/_/g," ").replace(/\b\w/g,c=>c.toUpperCase());
  /* Preferred display label: taxonomy entry label (with parent
     category if any) → humanized legacy task_type string. */
  const displayType=_taxEntry
    ?(_taxParent?`${_taxLbl(_taxParent)} · ${_taxLbl(_taxEntry)}`:_taxLbl(_taxEntry))
    :(task.taskType?fmtStatus(task.taskType):"");

  const ctx=task.contextPayload||{};
  const facilityIssue=(task.sourceModule==="Facility"&&ctx.maintenanceId)?maintenance.find(m=>m.id===ctx.maintenanceId):null;
  const facilitySupplier=(facilityIssue&&ctx.supplierId)?suppliers.find(s=>s.id===ctx.supplierId):null;
  const grocItems=(task.taskType==="grocery_run"&&ctx.items)?(ctx.items||[]):null;
  /* Transport tasks carry pickup/dropoff maps URLs + a directions URL on
     the context payload. Surface them as a dedicated section with
     clickable links instead of dumping raw URLs through the generic
     renderer below. */
  const transportCtx=(task.sourceModule==="Transport"&&(ctx.pickupLoc||ctx.dropoffLoc||ctx.pickupMapsUrl||ctx.dropoffMapsUrl||ctx.directionsUrl))
    ?{pickupLoc:ctx.pickupLoc,dropoffLoc:ctx.dropoffLoc,pickupMapsUrl:ctx.pickupMapsUrl,dropoffMapsUrl:ctx.dropoffMapsUrl,directionsUrl:ctx.directionsUrl,date:ctx.date,time:ctx.time}
    :null;
  const TRANSPORT_HANDLED_KEYS=new Set(["pickupLoc","dropoffLoc","pickupMapsUrl","dropoffMapsUrl","directionsUrl","transportIds","date","time","pickupPlaceId","dropoffPlaceId","pickupLat","pickupLng","dropoffLat","dropoffLng","riderId","groupId"]);
  /* Transport-linked lookups: rider / driver / vehicle / recurring come
     from the linked transport_request record (driverId, vehicleId and
     frequency aren't denormalized into contextPayload). Staff see all
     ride context in one modal, eliminating the need for a separate
     Rides panel once this covers every field.*/
  const transportRec=task.sourceModule==="Transport"
    ?(state.transportRequests||[]).find(r=>
        (task.contextPayload?.transportIds||[]).includes(r.id)
        ||(task.linkedEntities||[]).some(le=>le.type==="transport_request"&&le.id===r.id))
    :null;
  const transportRiderId=transportRec?.userId||ctx.riderId||null;
  const transportRider =transportRiderId?(state.users||[]).find(u=>u.id===transportRiderId):null;
  const transportDriver=transportRec?.driverId?(state.users||[]).find(u=>u.id===transportRec.driverId):null;
  const transportVehicle=transportRec?.vehicleId?(state.vehicles||[]).find(v=>v.id===transportRec.vehicleId):null;
  const transportFreq=transportRec?.isRecurring?(transportRec.frequencyLabel||""):"";
  /* Raw identifier keys (projectId, workstreamId, maintenanceId, …)
     are plumbing for analytics/wiring, not useful in the UI. Hide
     them from the generic context panel — the matching *Name field
     on the same payload already carries the readable label. */
  const ID_KEY_RE=/(Id|ID|Ids|IDs)$/;
  const genericCtxKeys=(ctx&&Object.keys(ctx).length>0&&task.sourceModule!=="Facility")
    ?Object.entries(ctx).filter(([k,v])=>v&&!["groceryIds","items","itemCount"].includes(k)&&!ID_KEY_RE.test(k)&&!(transportCtx&&TRANSPORT_HANDLED_KEYS.has(k)))
    :[];
  /* Match any http(s) URL in a value so the generic renderer can turn
     it into a tappable link — covers modules we don't have a dedicated
     section for yet. */
  const URL_RE=/^https?:\/\/\S+$/i;

  return(
    <Modal><div className="stf-detail-overlay" onClick={onClose}>
      <div className="stf-detail-sheet" onClick={e=>e.stopPropagation()}>
        <div className="stf-detail-handle"/>
        <div className="stf-detail-header">
          <div style={{flex:1,minWidth:0}}>
            <div className="stf-detail-title" style={{wordBreak:"break-word"}}>{isRTL&&task.titleAr?task.titleAr:task.title}</div>
            <div className="stf-detail-sub">{fmtStatus(task.sourceModule||"")}{displayType?` · ${displayType}`:""}{showTaskId?` · ${task.id}`:""} <span style={{cursor:"pointer",fontSize:10,color:"var(--g300)",marginInlineStart:4}} onClick={e=>{e.stopPropagation();setShowTaskId(p=>!p);}}>{showTaskId?(isRTL?"إخفاء المعرف":"Hide ID"):(isRTL?"عرض المعرف":"Show ID")}</span></div>
          </div>
          <button className="stf-detail-close" onClick={onClose}>&#10005;</button>
        </div>

        {/* Status + priority badges — mirrors the staff sheet's badge row */}
        <div style={{display:"flex",gap:6,flexWrap:"wrap",padding:"0 20px 12px"}}>
          <span className={`pill ${stPill(task.status)}`}>{fmtStatus(task.status)}</span>
          <span className={`pill ${priPill(task.priority)}`}>{task.priority.charAt(0).toUpperCase()+task.priority.slice(1)}</span>
          {task.escalationState&&task.escalationState!=="none"&&<span className="pill p-high">{isRTL?"تصعيد":"Escalation"}: {task.escalationState}</span>}
          {task.requiresApproval&&task.approvalStatus==="pending"&&<span className="pill p-wait">{isRTL?"بانتظار الموافقة":"Awaiting Approval"}</span>}
          {task.requiresExpense&&!task.expenseSubmitted&&task.status!=="completed"&&<span className="pill p-wait">{isRTL?"يتطلب مصروف":"Expense Required"}</span>}
        </div>

        {/* Tabs */}
        <div style={{display:"flex",gap:0,overflowX:"auto",padding:"0 20px",marginBottom:12,borderBottom:"1px solid var(--g100)"}}>
          {[
            {k:"details",l:isRTL?"التفاصيل":"Details"},
            {k:"comments",l:`${isRTL?"التعليقات":"Comments"} (${(task.comments||[]).length})`},
            {k:"activity",l:isRTL?"النشاط":"Activity"},
          ].map(tb=>(
            <div key={tb.k} onClick={()=>{setTab(tb.k);if(tb.k==="activity")loadLog();}} style={{padding:"8px 12px",fontSize:12,fontWeight:tab===tb.k?600:400,color:tab===tb.k?"var(--ink)":"var(--g400)",borderBottom:tab===tb.k?"2px solid var(--ink)":"2px solid transparent",cursor:"pointer",whiteSpace:"nowrap",transition:"all .15s"}}>{tb.l}</div>
          ))}
        </div>

        <div className="stf-detail-body">
          {/* ── DETAILS TAB ── */}
          {tab==="details"&&<>
            <div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"مسند إلى":"Assigned To"}</span><span className="stf-detail-row-value">{userName(users,task.assignedTo)||task.assignedToName||(isRTL?"غير معين":"Unassigned")}</span></div>
            {task.locId&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"الموقع":"Location"}</span><span className="stf-detail-row-value">{locName(locations,task.locId)||"—"}</span></div>}
            {task.dueDate&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"تاريخ الاستحقاق":"Due Date"}</span><span className="stf-detail-row-value mono">{task.dueDate}</span></div>}
            {task.scheduledTime&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"الوقت المحدد":"Scheduled"}</span><span className="stf-detail-row-value mono">{new Date(task.scheduledTime).toLocaleString(isRTL?"ar-SA":"en-US",{dateStyle:"medium",timeStyle:"short"})}</span></div>}
            {task.slaExpectedCompletion&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"موعد SLA":"SLA Completion"}</span><span className="stf-detail-row-value mono">{new Date(task.slaExpectedCompletion).toLocaleString(isRTL?"ar-SA":"en-US",{dateStyle:"medium",timeStyle:"short"})}</span></div>}
            {task.createdAt&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"أنشئت":"Created"}</span><span className="stf-detail-row-value">{new Date(task.createdAt).toLocaleDateString(isRTL?"ar-SA":"en-US",{dateStyle:"medium"})}{task.createdBy?` · ${userName(users,task.createdBy)||task.createdByName||task.createdBy}`:""}</span></div>}
            {task.escalationState&&task.escalationState!=="none"&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"مستوى التصعيد":"Escalation Level"}</span><span className="stf-detail-row-value" style={{color:"var(--red)"}}>{task.escalationLevel||0}</span></div>}

            {(()=>{
              /* Prefer the matching-language description if one was
                 entered at create time; else show the source language
                 with a TranslateInline affordance. Link-parsing only
                 runs on the source view; AI translations render plain. */
              const descLang = isRTL && task.descriptionAr ? task.descriptionAr : task.description;
              if (!descLang) return null;
              const isSourceMatch = (isRTL && task.descriptionAr) || (!isRTL && task.description && !/[؀-ۿ]/.test(task.description));
              return (
                <div style={{marginTop:12,fontSize:13,color:"var(--g600)",lineHeight:1.6,wordBreak:"break-word"}}>
                  {isSourceMatch
                    ? descLang.split(/(https?:\/\/[^\s]+)/g).map((part,i)=>/^https?:\/\//.test(part)?<a key={i} href={part} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(part);}} style={{color:"var(--blu)",textDecoration:"underline",wordBreak:"break-all",cursor:"pointer"}}>{part}</a>:part)
                    : <TranslateInline text={descLang} lang={lang} domain="task description"/>}
                </div>
              );
            })()}

            {/* Facility issue context */}
            {facilityIssue&&<>
              <div className="stf-detail-section">{isRTL?"تفاصيل مشكلة الصيانة":"Facility Issue Details"}</div>
              <div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"الفئة":"Category"}</span><span className="stf-detail-row-value" style={{textTransform:"capitalize"}}>{facilityIssue.category||facilityIssue.type||"—"}</span></div>
              <div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"النوع":"Kind"}</span><span className="stf-detail-row-value" style={{textTransform:"capitalize"}}>{facilityIssue.kind||"issue"}</span></div>
              <div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"حالة المشكلة":"Issue Status"}</span><span className="stf-detail-row-value">{facilityIssue.status}</span></div>
              <div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"الأولوية":"Priority"}</span><span className="stf-detail-row-value">{facilityIssue.priority}</span></div>
              {facilityIssue.reported&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"تاريخ البلاغ":"Reported"}</span><span className="stf-detail-row-value mono">{facilityIssue.reported}</span></div>}
              {facilityIssue.estimatedCost&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"التكلفة المتوقعة":"Est. Cost"}</span><span className="stf-detail-row-value mono">SAR {Number(facilityIssue.estimatedCost).toLocaleString()}</span></div>}
              {facilitySupplier&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"المورد":"Supplier"}</span><span className="stf-detail-row-value">{isRTL&&facilitySupplier.nameAr?facilitySupplier.nameAr:facilitySupplier.name} ({facilitySupplier.category})</span></div>}
              {facilityIssue.notes&&<div style={{marginTop:6,fontSize:12,color:"var(--g500)",lineHeight:1.6}}>{facilityIssue.notes}</div>}
            </>}

            {/* Grocery list context */}
            {grocItems&&<>
              <div className="stf-detail-section">{isRTL?"قائمة البقالة":"Grocery List"} ({grocItems.length} {isRTL?"عنصر":"items"})</div>
              {grocItems.map((gi,i)=>(
                <div key={gi.id||i} className="stf-detail-row">
                  <span className="stf-detail-row-label">{gi.item}</span>
                  <span className="stf-detail-row-value mono">{gi.qty}{gi.unit?` ${gi.unit}`:""}</span>
                </div>
              ))}
            </>}

            {/* Transport trip context — clickable pickup / dropoff / directions links. */}
            {transportCtx&&<>
              <div className="stf-detail-section">{isRTL?"تفاصيل الرحلة":"Trip Details"}</div>
              {(transportCtx.date||transportCtx.time)&&<div className="stf-detail-row"><span className="stf-detail-row-label">{isRTL?"الموعد":"When"}</span><span className="stf-detail-row-value mono">{transportCtx.date||""}{transportCtx.time?` · ${transportCtx.time}`:""}</span></div>}
              {transportCtx.pickupLoc&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"نقطة الانطلاق":"Pickup"}</span>
                <span className="stf-detail-row-value" style={{wordBreak:"break-word"}}>{transportCtx.pickupMapsUrl
                  ?<a href={transportCtx.pickupMapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(transportCtx.pickupMapsUrl);}} style={{color:"var(--blu)",textDecoration:"none"}}>{transportCtx.pickupLoc}</a>
                  :transportCtx.pickupLoc}</span>
              </div>}
              {transportCtx.dropoffLoc&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"الوجهة":"Dropoff"}</span>
                <span className="stf-detail-row-value" style={{wordBreak:"break-word"}}>{transportCtx.dropoffMapsUrl
                  ?<a href={transportCtx.dropoffMapsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(transportCtx.dropoffMapsUrl);}} style={{color:"var(--blu)",textDecoration:"none"}}>{transportCtx.dropoffLoc}</a>
                  :transportCtx.dropoffLoc}</span>
              </div>}
              {transportCtx.directionsUrl&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"الاتجاهات":"Directions"}</span>
                <span className="stf-detail-row-value"><a href={transportCtx.directionsUrl} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(transportCtx.directionsUrl);}} style={{color:"var(--blu)",textDecoration:"none"}}>{isRTL?"فتح الخريطة":"Open Map"}</a></span>
              </div>}
              {transportRider&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"الراكب":"Rider"}</span>
                <span className="stf-detail-row-value">{isRTL&&transportRider.nameAr?transportRider.nameAr:transportRider.name}</span>
              </div>}
              {transportDriver&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"السائق":"Driver"}</span>
                <span className="stf-detail-row-value">{isRTL&&transportDriver.nameAr?transportDriver.nameAr:transportDriver.name}</span>
              </div>}
              {transportVehicle&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"المركبة":"Vehicle"}</span>
                <span className="stf-detail-row-value">{[transportVehicle.make,transportVehicle.model,transportVehicle.plate].filter(Boolean).join(" · ")||transportVehicle.id}</span>
              </div>}
              {transportFreq&&<div className="stf-detail-row">
                <span className="stf-detail-row-label">{isRTL?"التكرار":"Recurring"}</span>
                <span className="stf-detail-row-value"><span className="pill p-gold" style={{fontSize:10}}>↻ {transportFreq}</span></span>
              </div>}
            </>}

            {/* Generic context for other modules */}
            {!facilityIssue&&!grocItems&&genericCtxKeys.length>0&&<>
              <div className="stf-detail-section">{isRTL?"سياق إضافي":"Additional Context"}</div>
              {genericCtxKeys.map(([k,v])=>{
                const str=typeof v==="object"?JSON.stringify(v):String(v);
                const isUrl=typeof v==="string"&&URL_RE.test(v);
                return(
                  <div key={k} className="stf-detail-row">
                    <span className="stf-detail-row-label">{k.replace(/([A-Z])/g," $1").replace(/^./,c=>c.toUpperCase())}</span>
                    <span className="stf-detail-row-value" style={{wordBreak:"break-word"}}>{isUrl
                      ?<a href={v} target="_blank" rel="noopener noreferrer" onClick={e=>{e.preventDefault();openExternalUrl(v);}} style={{color:"var(--blu)",textDecoration:"none"}}>{str}</a>
                      :str}</span>
                  </div>
                );
              })}
            </>}

            {/* Approval action bar */}
            {task.requiresApproval&&task.approvalStatus==="pending"&&canEdit&&<div style={{marginTop:16,padding:"12px 14px",background:"var(--gold-bg)",border:"1px solid var(--gold-bd)",borderRadius:12,fontSize:13}}>
              <div style={{fontWeight:600,color:"var(--gold)",marginBottom:8}}>{isRTL?"بانتظار الموافقة":"Awaiting approval"}</div>
              <div style={{display:"flex",gap:8}}>
                <button className="btn btn-p btn-sm" onClick={()=>{hapticSuccess();doApproval("approve");}} disabled={!!actionLoading}>{actionLoading==="approve"?"…":(isRTL?"موافقة":"Approve")}</button>
                <button className="btn btn-g btn-sm" onClick={()=>{hapticTap();doApproval("reject");}} disabled={!!actionLoading}>{actionLoading==="reject"?"…":(isRTL?"رفض":"Reject")}</button>
              </div>
            </div>}

            {/* Status transition buttons — matches the staff sheet's action row pattern.
                When the task requires an expense, the transition API returns
                expense_required on Complete and the handler auto-opens the
                TaskExpenseModal — so no separate "Submit Expense" prompt is
                rendered here; the Expense Required pill in the header row
                is enough signal. */}
            {canEdit&&available.length>0&&<div style={{marginTop:16,display:"grid",gridTemplateColumns:available.length===1?"1fr":"1fr 1fr",gap:8}}>
              {available.map(s=>(
                <button key={s} className={`btn btn-sm ${s==="completed"?"btn-p":s==="cancelled"?"btn-d":"btn-g"}`}
                  style={{width:"100%",padding:"10px 0",textAlign:"center",display:"flex",alignItems:"center",justifyContent:"center",...(s==="cancelled"?{background:"var(--red-bg)",border:"1px solid var(--red-bd)",color:"var(--red)"}:{})}}
                  onClick={()=>doTransition(s)} disabled={!!actionLoading}>
                  {actionLoading===s?"…":(s==="in_progress"&&task.status==="completed"?(isRTL?"إعادة فتح":"Reopen"):fmtStatus(s))}
                </button>
              ))}
            </div>}

            {!canEdit&&<div style={{marginTop:12,fontSize:12,color:"var(--g400)",fontStyle:"italic"}}>{isRTL?"وصولك لهذه المهمة للقراءة فقط.":"You have read-only access to this task."}</div>}
          </>}

          {/* ── COMMENTS TAB ── */}
          {tab==="comments"&&<div>
            {(task.comments||[]).map((c,i)=>(
              <div key={c.id||i} style={{padding:"10px 0",borderBottom:"1px solid var(--g100)"}}>
                <div style={{fontSize:12,fontWeight:600,color:"var(--g600)"}}>{c.byName||userName(users,c.by)||c.by} <span style={{fontWeight:400,color:"var(--g400)",fontSize:11,marginInlineStart:6}}>{c.at?new Date(c.at).toLocaleString(isRTL?"ar-SA":"en-US"):""}</span></div>
                <div style={{fontSize:13,marginTop:4,lineHeight:1.5,wordBreak:"break-word"}}><TranslateInline text={c.text} lang={lang} domain="comment"/></div>
              </div>
            ))}
            {!(task.comments||[]).length&&<div style={{padding:"20px 0",textAlign:"center",color:"var(--g400)",fontSize:13}}>{isRTL?"لا توجد تعليقات بعد.":"No comments yet."}</div>}
            {canEdit&&<div style={{display:"flex",gap:8,marginTop:12}}>
              <input className="fi" style={{flex:1}} value={comment} onChange={e=>setComment(e.target.value)} placeholder={isRTL?"أضف تعليقاً...":"Add a comment..."} onKeyDown={e=>{if(e.key==="Enter")addComment();}}/>
              <button className="btn btn-p btn-sm" onClick={addComment}>{isRTL?"إرسال":"Post"}</button>
            </div>}
            {!canEdit&&<div style={{fontSize:12,color:"var(--g400)",marginTop:12,fontStyle:"italic"}}>{isRTL?"وصولك لهذه المهمة للقراءة فقط.":"You have read-only access to this task."}</div>}
          </div>}

          {/* ── ACTIVITY TAB ── */}
          {tab==="activity"&&<div>
            {activityLog.map((log,i)=>{
              const isUserRef=log.action==="assigned"||log.action==="reassigned";
              const det=log.details||{};
              const fmtVal=(id,storedName)=>id?(userName(users,id)||storedName||id):id;
              const oldDisp=isUserRef?fmtVal(log.oldValue,det.oldName||det.old_name):log.oldValue;
              const newDisp=isUserRef?fmtVal(log.newValue,det.newName||det.new_name||det.assignedToName||det.assigned_to_name):log.newValue;
              return(
                <div key={log.id||i} style={{padding:"8px 0",borderBottom:"1px solid var(--g100)",fontSize:12}}>
                  <div>
                    <span style={{fontWeight:600}}>{log.actorName||userName(users,log.actorId)||log.actorId}</span>{" "}
                    <span style={{color:"var(--g500)"}}>{log.action.replace(/_/g," ")}</span>
                    {log.oldValue&&<span style={{color:"var(--g400)"}}>{" "}{oldDisp} → {newDisp}</span>}
                  </div>
                  <div style={{fontSize:11,color:"var(--g400)",marginTop:2}}>{new Date(log.createdAt).toLocaleString(isRTL?"ar-SA":"en-US")}</div>
                </div>
              );
            })}
            {!activityLog.length&&<div style={{padding:"20px 0",textAlign:"center",color:"var(--g400)",fontSize:13}}>{logLoaded?(isRTL?"لا يوجد نشاط بعد.":"No activity yet."):(isRTL?"جارٍ التحميل...":"Loading...")}</div>}
          </div>}
        </div>
      </div>
      {showExpense&&<TaskExpenseModal state={state} dispatch={dispatch} task={task} onClose={()=>setShowExpense(false)} onUpdate={onUpdate}/>}
    </div></Modal>
  );
}

export default TaskDetailModal;
